/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Home/components/DataCollectionSection.tsx.
 */

import React from "react";
import "./DataCollectionSection.css";

interface DataCard {
  icon: string; // Image path
  title: string;
  description: string;
  items: string[];
  note?: string;
  subheading?: string;
}

const DataCollectionSection: React.FC = () => {
  const dataCards: DataCard[] = [
    {
      icon: "/images/contributor_information.PNG",
      title: "Contributor information",
      description: "This section captures details about the data contributor, enabling transparency and accountability.",
      items: [
        "Contributor name and affiliation",
        "Organization / institution",
        "Contact details",
        "Role (manufacturer, researcher, consultant, etc.)",
      ],
      note: "This information is used only for data review and traceability and is not publicly disclosed without consent.",
    },
    {
      icon: "/images/publication_details.PNG",
      title: "Publication details",
      description: "If the dataset has been published previously, contributors are encouraged to provide:",
      items: [
        "Publication title",
        "Journal / report name",
        "Year of publication",
        "DOI or reference link",
      ],
      note: "Published datasets help strengthen data credibility.",
    },
    {
      icon: "/images/background_information.PNG",
      title: "Background information",
      description: "This section captures the context in which the data was generated.",
      items: [
        "Data collection location (plant, region, or country)",
        "Time period of data collection",
        "Product description and specifications",
        "Functional unit and system boundary",
      ],
      note: "This metadata ensures that the dataset can be correctly filtered by users based on temporal, geographical, technological, and manufacturer specificity.",
    },
    {
      icon: "/images/life_cycle_stage_data.PNG",
      title: "Life cycle stage data",
      description: "Contributors can enter material, fuel, and embodied impact data corresponding to specific life-cycle stages, in alignment with standard LCA frameworks.",
      items: [
        "A1 - Raw Material Extraction",
        "A2 - Transport",
        "A3 - Manufacturing",
        "Aggregated A1-A3: Combined impacts across the production stage, where disaggregated data is unavailable.",
      ],
      subheading: "Production Stage (A1-A3)",
    },
  ];

  return (
    <section className="home-section">
      <h2 className="section-heading">What data does ICOMEC collect?</h2>
      <p className="section-description">
        ICOMEC collects material-level life cycle inventory (LCI) and embodied carbon data.
      </p>
      <div className="home-info-cards-grid">
        {dataCards.map((card, index) => (
          <div key={index} className="home-info-card">
            <div className="home-info-card-header">
              <div className="home-info-card-icon">
                <img src={card.icon} alt={card.title} />
              </div>
              <h3 className="home-info-card-title">{card.title}</h3>
            </div>
            <p className="home-info-card-description">{card.description}</p>
            <ul className="home-info-card-list">
              {card.items.map((item, itemIndex) => (
                <li key={itemIndex}>{item}</li>
              ))}
            </ul>
            {card.note ? <p className="home-info-card-note">{card.note}</p> : null}
          </div>
        ))}
      </div>
    </section>
  );
};

/** React component — Data Collection Section. */
export default DataCollectionSection;

